import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, Download } from 'lucide-react';
import api from '../api/axios';
import { escapeCsvCell } from '../utils/csv';

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

export default function EyeAIUsageReport() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [cdps, setCdps] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [selectedCdp, setSelectedCdp] = useState('');
  const [userTimeZone, setUserTimeZone] = useState('UTC');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterDevId, setFilterDevId] = useState('');
  const [filterPatiName, setFilterPatiName] = useState('');
  const [appliedFilterDevId, setAppliedFilterDevId] = useState('');
  const [appliedFilterPatiName, setAppliedFilterPatiName] = useState('');

  const NUMERIC_COLUMNS = new Set(['Rate', 'Qty', 'Credit', 'Debit', 'BalCryFrw']);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    let data = reportData;
    if (appliedFilterDevId.trim()) {
      const q = appliedFilterDevId.trim().toLowerCase();
      data = data.filter(row => String(row.DevID ?? '').toLowerCase().includes(q));
    }
    if (appliedFilterPatiName.trim()) {
      const q = appliedFilterPatiName.trim().toLowerCase();
      data = data.filter(row => String(row.PatiName ?? '').toLowerCase().includes(q));
    }
    if (!sortColumn || data.length === 0) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      let cmp;
      if (NUMERIC_COLUMNS.has(sortColumn)) {
        cmp = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
      } else {
        cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [reportData, appliedFilterDevId, appliedFilterPatiName, sortColumn, sortDirection]);

  useEffect(() => {
    const fetchCdps = async () => {
      try {
        const response = await api.get('/webapi/cdpusagereport');
        if (response.data && response.data.cdps) {
          setCdps(response.data.cdps);
          setUserTimeZone(response.data.userTimeZone || 'UTC');
          setSelectedCdp(response.data.cdps[0]?.cdpPk || '');
          // if (response.data.cdps.length > 0) {
          //   setSelectedCdp(response.data.cdps[0].cdpPk);
            
          // }
        }
      } catch (err) {
        setError('Failed to fetch CDPs.');
        console.error('Error fetching CDPs:', err);
      }
    };
    fetchCdps();
  }, []);

  // Detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFetchReport = async () => {
    if (!selectedCdp) {
      setError('Please select a CDP before submitting.');
      return;
    }
    setError(null);
    setIsLoading(true);
    setReportData([]);
    setSortColumn(null);
    setSortDirection('asc');

    try {
      const response = await api.get('/webapi/cdpusagereport', {
        params: {
          CDP_pk: selectedCdp,
          start_date: startDate,
          end_date: endDate,
        },
      });
      setReportData(response.data || []);
      setAppliedFilterDevId(filterDevId);
      setAppliedFilterPatiName(filterPatiName);
    } catch (err) {
      setError('Failed to fetch usage report.');
      console.error('Error fetching usage report:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!reportData || reportData.length === 0) {
      setError('No report data to download.');
      return;
    }

    const headers = ['Date', 'Operator', 'Device', 'Transaction Code', 'Report ID', 'Patient Name', 'Rate', 'Qty', 'Credit', 'Debit', 'Balance'];
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => {
        return [
          escapeCsvCell(row.cUTCTime),
          escapeCsvCell(row.UserID),
          escapeCsvCell(row.DevID),
          escapeCsvCell(row.TrxCode),
          escapeCsvCell(row.ReportID),
          escapeCsvCell(row.PatiName),
          escapeCsvCell(row.Rate || 0),
          escapeCsvCell(row.Qty || 0),
          escapeCsvCell(row.Credit || 0),
          escapeCsvCell(row.Debit || 0),
          escapeCsvCell(row.BalCryFrw || 0)
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `cdp_usage_report_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className=" mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800">Eye-AI Usage Report</h2>
          <span className="text-sm text-gray-600">Time zone: {userTimeZone}</span>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4">CDP's Activitiy Report</h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">By CDP</label>
            <div className="relative w-full max-w-md">
              <select 
                value={selectedCdp}
                onChange={(e) => setSelectedCdp(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {cdps.map((cdp) => (
                  <option key={cdp.cdpPk} value={cdp.cdpPk}>{cdp.userId} - {cdp.company}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Report date range selection</label>
            <div className="flex gap-[105px] w-full max-w-md">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full  px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full  px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

         <div className="mb-6">
            
            <div className="flex gap-4 w-full max-w-2xl">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Device ID</label>
                <input
                  type="text"
                  value={filterDevId}
                  onChange={(e) => setFilterDevId(e.target.value)}
                  placeholder="e.g. DEMO001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Patient Name</label>
                <input
                  type="text"
                  value={filterPatiName}
                  onChange={(e) => setFilterPatiName(e.target.value)}
                  placeholder="Search patient name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleFetchReport}
              disabled={isLoading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-300 disabled:cursor-not-allowed"
            >
              Submit
            </button>
            <button
              onClick={handleDownloadCSV}
              disabled={isLoading || !reportData || reportData.length === 0}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Download size={20} />
              CSV
            </button>
          </div>

           

        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    { label: 'Date', key: 'cUTCTime' },
                    { label: 'Operator', key: 'UserID' },
                    { label: 'Device', key: 'DevID' },
                    { label: 'Transaction Code', key: 'TrxCode' },
                    { label: 'Report ID', key: 'ReportID' },
                    { label: 'Patient Name', key: 'PatiName' },
                    { label: 'Rate', key: 'Rate' },
                    { label: 'Qty', key: 'Qty' },
                    { label: 'Credit', key: 'Credit' },
                    { label: 'Debit', key: 'Debit' },
                    { label: 'Balance', key: 'BalCryFrw' },
                  ].map(({ label, key }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {sortColumn === key ? (
                          sortDirection === 'asc'
                            ? <ChevronUp className="w-4 h-4 text-indigo-600" />
                            : <ChevronDown className="w-4 h-4 text-indigo-600" />
                        ) : (
                          <ChevronsUpDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan="11" className="text-center py-10">
                      <div className="text-gray-500">Loading report data...</div>
                    </td>
                  </tr>
                ) : sortedData.length > 0 ? (
                  sortedData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{row.cUTCTime}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.UserID}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.DevID}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.TrxCode}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.ReportID ? row.ReportID : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.PatiName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.Rate}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.Qty}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.Credit}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.Debit}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.BalCryFrw}</td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center py-10 text-gray-500">No data to display.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-all flex items-center justify-center z-50"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
